import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { StepProps } from "../../../../components/Stepper"
import { useAuth } from "../../../../hooks/useAuth"
import { useRequest } from "../../../../hooks/useRequest"
import { useTimer } from "../../../../hooks/useTimer"
import { appStore } from "../../../../store/appStore"
import { userStore } from "../../../../store/userStore"
import { applyPendingLocation, getAuthCityId } from "../../../../lib/locationSwitch"
import { clearAuthDraft } from "../../../../lib/authDraft"
import { AuthData } from "../../../../types/steps/auth"
import AuthSheetLayout, { AuthSheetButton } from "../../../../features/auth/AuthSheetLayout"
import { AuthCaption, AuthPhoneInline, AuthPhoneNumber } from "../../../../features/auth/AuthShell"
import { authCaptionStyle } from "../../../../features/auth/authTheme"

function formatPhone(value?: string | number) {
    const digits = String(value ?? "").replace(/\D/g, "")
    if (!digits) return ""

    if (digits.length === 11 && digits.startsWith("8")) {
        return `8 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
    }

    if (digits.length === 11 && digits.startsWith("7")) {
        return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
    }

    if (digits.length === 10) {
        return `8 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`
    }

    return `+${digits}`
}

export default function Call({ params, navigate, goBack }: StepProps<AuthData>) {
    const { requestData } = useRequest()
    const { checkCode, sendCode } = useAuth()
    const { lastSmsCodeAt } = appStore()
    const { setUser } = userStore()
    const router = useNavigate()
    const { remainSec } = useTimer(lastSmsCodeAt)
    const [loadingSms, setLoadingSms] = useState(false)

    const callNumber = useMemo(() => formatPhone(params.resData?.confirmationNumber), [params.resData?.confirmationNumber])
    const userPhone = useMemo(() => formatPhone(`+7${params.phone ?? ""}`), [params.phone])

    const checkCall = async () => {
        if (!params.phone || !params.resData?.callId) return

        const res = await requestData<{ code: string }>({
            type: "POST",
            path: "newtel/status",
            sendData: {
                phone: `7${params.phone}`,
                city_id: getAuthCityId(),
                call_id: params.resData.callId,
            },
        })

        if (res.status !== "success") return

        const user = await checkCode(res.data.code, params.registered!, params.phone)

        if (params.registered) {
            setUser(user)
            clearAuthDraft()
            applyPendingLocation()
            router(-1)
        } else {
            navigate("user", { phoneVerification: user.phone_verification, reauth: params.reauth })
        }
    }

    const handleRequestSms = async () => {
        if (!params.phone || loadingSms) return

        try {
            setLoadingSms(true)
            const { registered, resData } = await sendCode(params.phone, "sms")
            navigate("code", { phone: params.phone, registered, resData, reauth: params.reauth })
        } finally {
            setLoadingSms(false)
        }
    }

    useEffect(() => {
        const id = setInterval(checkCall, 5000)
        return () => clearInterval(id)
    }, [])

    const smsRequest = (
        <AuthSheetButton
            variant="primary"
            disabled={Boolean(remainSec) || loadingSms}
            onClick={handleRequestSms}>
            {loadingSms
                ? "Отправка..."
                : remainSec
                  ? `Запросить СМС через ${remainSec} сек`
                  : "Запросить СМС"}
        </AuthSheetButton>
    )

    const hasQr = Boolean(params.resData?.qrCodeUri)

    const mobileInfoBox = (
        <div className="rounded-[20px] border border-white/10 bg-white/[0.06] px-4 py-3">
            <div className="flex items-start gap-3 text-left">
                <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#349be6]/20 text-[16px] leading-none text-[#349be6]"
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                    i
                </div>
                <div className="min-w-0 text-[13px] leading-relaxed text-white/80" style={authCaptionStyle}>
                    <div>
                        Звонок на <AuthPhoneInline>{callNumber}</AuthPhoneInline>
                    </div>
                    <div>
                        Звоните с номера <AuthPhoneInline>{userPhone}</AuthPhoneInline>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <AuthSheetLayout
            title="Вход звонком"
            onBack={goBack}
            footer={
                <>
                    <div className={hasQr ? "md:hidden" : undefined}>
                        <AuthSheetButton
                            variant="secondary"
                            href={`tel:+${params.resData?.confirmationNumber ?? ""}`}>
                            Бесплатный звонок
                        </AuthSheetButton>
                    </div>
                    {smsRequest}
                </>
            }>
            <div className="flex flex-col gap-4 md:gap-5">
                <div className="flex flex-col gap-4 md:hidden">
                    <AuthPhoneNumber className="text-left">{userPhone}</AuthPhoneNumber>
                    <AuthCaption className="text-left">
                        Нажмите «Бесплатный звонок», дождитесь ответа и сброса, затем вернитесь в приложение.
                    </AuthCaption>
                    {mobileInfoBox}
                </div>

                {hasQr ? (
                    <div className="hidden flex-col items-center gap-4 text-center md:flex md:gap-5">
                        <AuthCaption>Отсканируйте QR-код камерой телефона</AuthCaption>
                        <img
                            src={params.resData!.qrCodeUri}
                            alt="QR-код для звонка"
                            className="h-[200px] w-[200px] rounded-[20px] bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.16)]"
                        />
                        {callNumber && <AuthPhoneNumber>{callNumber}</AuthPhoneNumber>}
                        <AuthCaption className="max-w-[340px]">
                            Позвоните на номер выше с телефона
                            <br />
                            <AuthPhoneInline>
                                <span className="whitespace-nowrap">{userPhone}</span>
                            </AuthPhoneInline>
                            <br />
                            Звонок бесплатный
                        </AuthCaption>
                    </div>
                ) : (
                    <div className="hidden flex-col gap-4 md:flex">
                        {callNumber && <AuthPhoneNumber className="text-left">{callNumber}</AuthPhoneNumber>}
                        <AuthCaption className="text-left">
                            Позвоните на номер выше с телефона
                            <br />
                            <AuthPhoneInline>
                                <span className="whitespace-nowrap">{userPhone}</span>
                            </AuthPhoneInline>
                            <br />
                            Звонок бесплатный
                        </AuthCaption>
                    </div>
                )}
            </div>
        </AuthSheetLayout>
    )
}
